import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  TreatmentPlanDto,
  AssignedPatientResponse,
  WeekDateRangeResponse,
  PatientResponse,
  InterventionGroupDto
} from '../../api/generated';
import { PatientAssignmentControllerApi } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { flattenGroups } from '../../utils/ganttUtils';
import { useAppDispatch } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { useRoomProperties } from '../../hooks/useRoomProperties';
import { useScrollLock } from '../../hooks/useScrollLock';
import { formatDateStr } from '../../utils/dateUtils';
import ChevronIcon from '../../assets/icons/chevron.svg?react';
import { PatientList } from './PatientList';
import { PatientDetailView, type PatientDetailViewHandle } from './PatientDetailView';
import { resolveDisplayedPatient } from './displayedPatient';
import { TreatmentTimeline } from './TreatmentTimeline';
import { Modal } from '../UI';
import { Button } from '../UI/base/Button';
import {
  INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_CSS_VAR,
  INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_MS
} from './interventionGroupDrawerConstants';
import styles from './InterventionGroupDrawer.module.css';

export type EnrichedPatient = AssignedPatientResponse & { name?: string };

interface InterventionGroupDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  treatmentPlan?: TreatmentPlanDto;
  selectedGroup?: InterventionGroupDto;
  patients?: EnrichedPatient[];
  weeks?: WeekDateRangeResponse[];
  isEditMode?: boolean;
  onCapacityChange?: (capacity: number, groupIds: string[]) => void;
  onSelectGroup?: (groupId: string) => void;
  patientsById?: Record<string, PatientResponse>;
  addPatientOnOpen?: boolean;
  onAddPatientHandled?: () => void;
  locationId?: string;
  scenarioId?: string;
  onPatientRemoved?: () => void;
  onPatientAdded?: () => void;
}

// TODO: consolidate with PatientDetailsDrawer into a single drawer shell.
export const InterventionGroupDrawer = ({
  isOpen,
  onClose,
  treatmentPlan,
  selectedGroup,
  patients = [],
  weeks = [],
  isEditMode = false,
  onCapacityChange,
  onSelectGroup,
  patientsById = {},
  addPatientOnOpen,
  onAddPatientHandled,
  locationId,
  scenarioId,
  onPatientRemoved,
  onPatientAdded
}: InterventionGroupDrawerProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const drawerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const patientViewRef = useRef<PatientDetailViewHandle>(null);
  const slideOutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<PatientResponse | null>(null);
  const [isSliding, setIsSliding] = useState(false);
  const [initialEditing, setInitialEditing] = useState(false);

  useScrollLock(isOpen, drawerRef);

  // Was a second, hand-rolled copy of useRoomProperties' effect calling the
  // same endpoint. Consolidated onto the hook so this drawer owns a fetch that
  // can actually be retried — its own `isOpen` is `selectedTreatmentPlanId !==
  // null`, which does not change while navigating inside the drawer, so before
  // this a failure could only be escaped by closing the whole drawer.
  const { hasError: hasRoomPropertiesError, retry: retryRoomProperties } =
    useRoomProperties(isOpen);

  const handleAddPatient = useCallback(() => {
    const emptyPatient: PatientResponse = {
      id: '',
      name: '',
      cprNumber: '',
      patientRoomNeedIds: [],
      deleted: false
    };
    setSelectedPatient(emptyPatient);
    setInitialEditing(true);
    requestAnimationFrame(() => setIsSliding(true));
  }, []);

  const handleBackToList = useCallback(() => {
    setIsSliding(false);
    setInitialEditing(false);
    if (slideOutTimerRef.current !== null) clearTimeout(slideOutTimerRef.current);
    slideOutTimerRef.current = setTimeout(() => {
      setSelectedPatient(null);
      slideOutTimerRef.current = null;
    }, INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_MS);
  }, []);

  const [pendingDiscardAction, setPendingDiscardAction] = useState<(() => void) | null>(null);

  const guardDiscard = useCallback((action: () => void) => {
    if (patientViewRef.current?.isAddDirty()) {
      setPendingDiscardAction(() => action);
    } else {
      action();
    }
  }, []);

  const handleBack = useCallback(() => {
    if (patientViewRef.current?.goBack()) return;
    guardDiscard(handleBackToList);
  }, [handleBackToList, guardDiscard]);

  const handleCloseGuarded = useCallback(() => guardDiscard(onClose), [guardDiscard, onClose]);

  useEffect(
    () => () => {
      if (slideOutTimerRef.current !== null) clearTimeout(slideOutTimerRef.current);
    },
    []
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedPatient(null);
      setIsSliding(false);
    }
  }, [isOpen]);

  // The patient list can be scrolled when a patient view slides in over it;
  // without a reset the patient view opens mid-scroll with its top fields
  // (e.g. the add-patient name/CPR search) hidden above the fold.
  const isPatientViewOpen = selectedPatient !== null;
  useEffect(() => {
    if (isPatientViewOpen) contentRef.current?.scrollTo({ top: 0 });
  }, [isPatientViewOpen]);

  useEffect(() => {
    if (isOpen && addPatientOnOpen) {
      handleAddPatient();
      onAddPatientHandled?.();
    }
  }, [isOpen, addPatientOnOpen, onAddPatientHandled, handleAddPatient]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (selectedPatient) {
          handleBack();
        } else {
          handleCloseGuarded();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleCloseGuarded, selectedPatient, handleBack]);

  const handlePatientClick = (patientId: string) => {
    const patient = patientsById[patientId];
    if (patient && !patient.deleted) {
      setSelectedPatient(patient);
      setInitialEditing(false);
      requestAnimationFrame(() => setIsSliding(true));
    }
  };

  const handleRemovePatient = useCallback(
    async (patientId: string, groupIds: string[]) => {
      if (!locationId || !scenarioId || !treatmentPlan?.id || groupIds.length === 0) return;
      try {
        const config = createApiConfiguration();
        const api = new PatientAssignmentControllerApi(config);
        await api.removePatientFromGroups({
          locationId,
          scenarioId,
          treatmentPlanId: treatmentPlan.id,
          patientId,
          interventionGroupIdsRequest: { interventionGroupIds: groupIds }
        });
        onPatientRemoved?.();
      } catch {
        dispatch(showError({ message: t('remove-patient-error') }));
      }
    },
    [locationId, scenarioId, treatmentPlan?.id, onPatientRemoved, t, dispatch]
  );

  const handleAssignPatient = async (
    patientId: string,
    groupIds: string[],
    justCreated: boolean
  ): Promise<void> => {
    if (!locationId || !scenarioId || !treatmentPlan?.id || groupIds.length === 0) return;
    try {
      const config = createApiConfiguration();
      const api = new PatientAssignmentControllerApi(config);
      await api.assignPatientToGroups({
        locationId,
        scenarioId,
        treatmentPlanId: treatmentPlan.id,
        patientId,
        interventionGroupIdsRequest: { interventionGroupIds: groupIds }
      });
      onPatientAdded?.();
      handleBackToList();
    } catch {
      const msg = justCreated
        ? t('error-patient-created-not-assigned')
        : t('error-patient-not-assigned');
      dispatch(showError({ message: msg }));
    }
  };

  const flatGroups = useMemo(
    () => (treatmentPlan ? flattenGroups(treatmentPlan) : []),
    [treatmentPlan]
  );

  const timelineItems = useMemo(() => {
    let moduleCounter = 0;
    return [...flatGroups]
      .sort((a, b) => a.weekNumber - b.weekNumber || (a.order ?? 0) - (b.order ?? 0))
      .map((g) => {
        moduleCounter++;
        return {
          id: g.id ?? '',
          name: g.name ?? '',
          week: g.weekNumber,
          icon: g.icon,
          moduleLabel: `M${String(moduleCounter)}`,
          interventions: g.interventions.map((i) => ({
            id: i.id,
            name: i.name,
            color: i.color
          }))
        };
      });
  }, [flatGroups]);

  const weekLookup = useMemo(() => new Map(weeks.map((w) => [w.weekNumber, w])), [weeks]);

  const selectedGroupHeader = useMemo(() => {
    if (!selectedGroup) return { title: '', dateLine: '' };
    const idx = timelineItems.findIndex((item) => item.id === selectedGroup.id);
    const ordinal = idx >= 0 ? idx + 1 : null;
    const fallbackLabel = ordinal !== null ? `M${String(ordinal)}` : t('treatment-plan-label');
    const title = selectedGroup.name?.trim() ? selectedGroup.name : fallbackLabel;
    const flat = flatGroups.find((g) => g.id === selectedGroup.id);
    const week = flat?.weekNumber;
    const dateLine =
      week != null
        ? `${t('week-label')} ${String(week)} · ${formatDateStr(weekLookup.get(week)?.startDate)}`
        : '';
    return { title, dateLine };
  }, [selectedGroup, timelineItems, flatGroups, weekLookup, t]);

  const weekRange = useMemo(() => {
    if (flatGroups.length === 0) return '';
    const weekNums = flatGroups.map((g) => g.weekNumber).filter((w) => w > 0);
    if (weekNums.length === 0) return '';
    const minWeek = Math.min(...weekNums);
    const maxWeek = Math.max(...weekNums);
    const prefix = t('week-label');
    return minWeek === maxWeek ? `${prefix} ${minWeek}` : `${prefix} ${minWeek}-${maxWeek}`;
  }, [flatGroups, t]);

  // Shadow patients are backend placeholders and must never surface in the UI —
  // they are represented by the aggregated available-seats row instead
  const patientList = useMemo(
    () =>
      patients
        .filter((p) => !p.shadow)
        .map((p) => ({
          id: p.patientId ?? '',
          name: p.name ?? '',
          practitioners: '',
          deleted: patientsById[p.patientId ?? '']?.deleted ?? false
        })),
    [patients, patientsById]
  );

  const assignedPatientIds = useMemo(
    () =>
      patients
        .filter((p) => !p.shadow)
        .map((p) => p.patientId)
        .filter((id): id is string => !!id),
    [patients]
  );

  const groupCapacity = selectedGroup?.capacity ?? 0;
  const treatmentPlanName = treatmentPlan?.name ?? '—';

  const displayedPatient = resolveDisplayedPatient(selectedPatient, patientsById);

  const drawerStyle = {
    [INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_CSS_VAR]: `${INTERVENTION_GROUP_DRAWER_TRANSITION_DURATION_MS}ms`
  } as CSSProperties;

  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={handleCloseGuarded} aria-hidden="true" />}
      <aside
        ref={drawerRef}
        className={`${styles.drawer} ${isOpen ? styles.open : ''}`}
        style={drawerStyle}
        aria-label={t('treatment-plan-label')}
      >
        <div className={styles.drawerHeader}>
          <div>
            {selectedPatient ? (
              <div className={styles.patientHeader}>
                <button
                  className={styles.backButton}
                  onClick={handleBack}
                  aria-label={t('go-back')}
                >
                  <span className={styles.backIcon}>
                    <ChevronIcon />
                  </span>
                </button>
                <span className={styles.drawerTitle}>
                  {displayedPatient?.name || t('add-patient')}
                </span>
              </div>
            ) : (
              <>
                <span className={styles.drawerTitle}>{selectedGroupHeader.title}</span>
                {selectedGroupHeader.dateLine && (
                  <span className={styles.drawerSubtitle}>{selectedGroupHeader.dateLine}</span>
                )}
                <span className={styles.drawerCaption}>
                  {treatmentPlanName}
                  {weekRange && ` · ${weekRange}`}
                </span>
              </>
            )}
          </div>
          <button className={styles.closeButton} onClick={handleCloseGuarded} aria-label={t('close-details')}>
            &times;
          </button>
        </div>
        <div ref={contentRef} className={styles.content}>
          <div className={styles.slideContainer}>
            <div className={`${styles.planView} ${selectedPatient ? styles.slideOut : ''}`}>
              <PatientList
                key={selectedGroup?.id ?? treatmentPlan?.id}
                patients={patientList}
                capacity={groupCapacity}
                plan={treatmentPlan}
                selectedGroup={selectedGroup}
                isEditMode={isEditMode}
                onCapacityChange={onCapacityChange}
                onPatientClick={handlePatientClick}
                onAddPatient={handleAddPatient}
                onRemovePatient={handleRemovePatient}
              />
              {/* Keyed by plan so expansion state never carries over to another plan */}
              <TreatmentTimeline
                key={treatmentPlan?.id}
                items={timelineItems}
                strokeColor={treatmentPlan?.strokeColor ?? 'var(--color-graphics-graph-second)'}
                fillColor={treatmentPlan?.fillColor ?? 'var(--color-graphics-graph-second)'}
                selectedId={selectedGroup?.id}
                onItemClick={onSelectGroup}
              />
            </div>
            {displayedPatient && (
              <div className={`${styles.patientView} ${isSliding ? styles.slideIn : ''}`}>
                <PatientDetailView
                  ref={patientViewRef}
                  patient={displayedPatient}
                  treatmentPlanName={treatmentPlanName}
                  weekRange={weekRange}
                  timelineItems={timelineItems}
                  weeks={weeks}
                  strokeColor={treatmentPlan?.strokeColor ?? 'var(--color-graphics-graph-second)'}
                  fillColor={treatmentPlan?.fillColor ?? 'var(--color-graphics-graph-second)'}
                  initialEditing={initialEditing}
                  locationId={locationId}
                  plan={treatmentPlan}
                  selectedGroup={selectedGroup}
                  assignedPatientIds={assignedPatientIds}
                  hasRoomPropertiesError={hasRoomPropertiesError}
                  onRetryRoomProperties={retryRoomProperties}
                  onAssignPatient={handleAssignPatient}
                />
              </div>
            )}
          </div>
        </div>
      </aside>
      {pendingDiscardAction && (
        <Modal
          title={t('discard-changes-title')}
          onClose={() => setPendingDiscardAction(null)}
          width={420}
          footer={
            <div className={styles.discardFooter}>
              <Button type="secondary" onClick={() => setPendingDiscardAction(null)}>
                {t('discard-changes-cancel')}
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  pendingDiscardAction();
                  setPendingDiscardAction(null);
                }}
              >
                {t('discard-changes-confirm')}
              </Button>
            </div>
          }
        >
          <p className={styles.discardMessage}>{t('discard-changes-message')}</p>
        </Modal>
      )}
    </>
  );
};
